import React, { type JSX } from 'react';

import SquarePenIcon from 'assets/icons/square-pen.inline.svg';
import PanelLeftIcon from 'assets/icons/panel-left.inline.svg';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import SidePeekIcon from 'assets/icons/side-peek.inline.svg';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import useChatAgentDialog from 'components/Dialogs/ChatAgentDialog/dispatcher';
import { trackingEvents } from 'containers/SimplifiAiAssistant/AiAssistant/aiEvents';
import { tracker } from 'app-components/utils/core';
import { isDevMode } from 'app-components/utils/core/config';
import { useChatSession } from 'components/ChatLauncher/chatSession';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { authSelectors } from 'app-components/flux/auth';
import { useAppSelector } from 'hooks/redux';
type ChatHeaderProps = {
  onClose: () => void;
  handleMinimize: () => void;
  handleDock: () => void;
  isDocked: boolean;
  setShowSideBarMenu: React.Dispatch<React.SetStateAction<boolean>>;
  handleRef: React.RefObject<HTMLDivElement | null>;
  isDefaultState?: boolean;
};

const ChatHeader = ({
  onClose,
  handleMinimize,
  handleDock,
  isDocked,
  setShowSideBarMenu,
  handleRef,
  isDefaultState,
}: ChatHeaderProps): JSX.Element => {
  const openChatAgentDialog = useChatAgentDialog();
  const { startNewConversation } = useChatSession();
  const [modelOverrideIds] = usePreferenceV2('aiModelOverrideIds', []);
  const qcsId = useAppSelector(authSelectors.getQCSId);
  const canSwitchAgent = Array.isArray(modelOverrideIds) && qcsId ? modelOverrideIds.includes(String(qcsId)) : false;

  // Detect platform for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdKey = isMac ? '⌘' : 'Ctrl';

  const handleNewConversation = async () => {
    tracker.track(trackingEvents.AIChatNew);
    tracker.track(trackingEvents.AIChatSaved);
    if (isDevMode() || canSwitchAgent) {
      openChatAgentDialog();
    } else {
      await startNewConversation();
    }
  };

  const handleOpenSidebar = () => {
    setShowSideBarMenu((prev) => !prev);
  };

  const handleClose = () => {
    tracker.track(trackingEvents.AIChatClosed);
    tracker.track(trackingEvents.AIChatSaved);
    onClose();
  };

  const theme = useTheme();
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 10,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(to bottom, ${theme.palette.background.paper} 80%, transparent)`,
          zIndex: 0,
          userSelect: 'none',
        }}
      />
      <Stack
        direction='row'
        alignItems='center'
        padding={2}
        ref={handleRef}
        sx={{
          position: 'relative',
          zIndex: 10,
          cursor: isDocked ? 'default' : 'grab',
          gap: 1,
        }}
      >
        <Stack gap={1} direction='row' alignItems='center' sx={{ flexShrink: 0 }}>
          <Tooltip title='Open Sidebar'>
            <IconButton
              id='ai-chat-header-sidebar'
              variant='tonal'
              size='small'
              onClick={handleOpenSidebar}
              sx={{ cursor: 'w-resize' }}
            >
              <PanelLeftIcon width={18} height={18} />
            </IconButton>
          </Tooltip>
          {!isDefaultState && (
            <Tooltip title='New Chat'>
              <Button
                id='ai-chat-header-new'
                variant='outlined'
                size='small'
                onClick={handleNewConversation}
                color='primary'
                startIcon={<SquarePenIcon width={18} height={18} />}
              >
                New
              </Button>
            </Tooltip>
          )}
          <Tooltip title='Quicken Assist is currently in beta and may make mistakes. Please double-check your results.'>
            <Chip label='Beta' variant='tonal' color='warning' size='small' sx={{ cursor: 'pointer' }} />
          </Tooltip>
        </Stack>

        <Box sx={{ minWidth: 0, flexGrow: 1, overflow: 'hidden' }} />

        <Stack gap={0.5} direction='row' alignItems='center' justifyContent='flex-end' sx={{ flexShrink: 0 }}>
          <Tooltip title={isDocked ? `Close split view (${cmdKey}.)` : `Open split view (${cmdKey}.)`}>
            <IconButton
              id='ai-chat-header-split-view'
              variant='tonal'
              size='small'
              onClick={handleDock}
              color={isDocked ? 'primary' : 'default'}
            >
              <SidePeekIcon width={20} height={20} />
            </IconButton>
          </Tooltip>
          {!isDocked && (
            <Tooltip title='Minimize'>
              <IconButton id='ai-chat-header-minimize' variant='tonal' size='small' onClick={handleMinimize}>
                <RemoveRoundedIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Close'>
            <IconButton id='ai-chat-header-close' variant='tonal' size='small' onClick={handleClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ChatHeader;
